import { Offcanvas } from 'bootstrap'
import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface DrawerProps {
  show: boolean
  onClose: () => void
  title?: string
  subtitle?: ReactNode
  width?: 'md' | 'lg'
  children: ReactNode
}

/**
 * Right-side drawer, per Frontend Spec §5.4: candidate detail and the
 * applications-page detail view must be a drawer (not a center modal) "so
 * recruiters can keep the search list visible." Built on Bootstrap's
 * offcanvas, same ref-driven pattern as Modal.
 */
export function Drawer({ show, onClose, title, subtitle, width = 'md', children }: DrawerProps) {
  const elementRef = useRef<HTMLDivElement>(null)
  const instanceRef = useRef<Offcanvas | null>(null)

  useEffect(() => {
    const el = elementRef.current
    if (!el) return

    const instance = new Offcanvas(el, { backdrop: true, scroll: false })
    instanceRef.current = instance

    const handleHidden = () => onClose()
    el.addEventListener('hidden.bs.offcanvas', handleHidden)

    return () => {
      el.removeEventListener('hidden.bs.offcanvas', handleHidden)
      instance.dispose()
      instanceRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const instance = instanceRef.current
    if (!instance) return
    if (show) {
      instance.show()
    } else {
      instance.hide()
    }
  }, [show])

  return createPortal(
    <div
      className="offcanvas offcanvas-end"
      ref={elementRef}
      tabIndex={-1}
      style={{ width: width === 'lg' ? '560px' : '420px' }}
      aria-hidden={!show}
    >
      <div className="offcanvas-header border-bottom">
        <div className="flex-fill">
          {title && <h5 className="offcanvas-title mb-0">{title}</h5>}
          {subtitle && <div className="mt-1">{subtitle}</div>}
        </div>
        <button type="button" className="btn-close ms-2" aria-label="Close" onClick={onClose} />
      </div>
      <div className="offcanvas-body p-0">{children}</div>
    </div>,
    document.body,
  )
}
